import {
  forwardRef,
  FC,
  Ref,
  useEffect,
  MouseEvent,
  KeyboardEvent,
  PropsWithoutRef,
  MouseEventHandler,
  KeyboardEventHandler
} from 'react';
import styled, { css } from 'styled-components';
import { readableColor, hideVisually, mix } from 'polished';

import FormField from '../FormField';
import { FormControlProps, StyledFormControl } from '../FormControl';
import { defaultThemeProp } from '../../theme';
import { tryCatch } from '../../utils/utils';
import { BaseProps, ForwardProps, NoChildrenProp } from '../../types';
import { useConsolidatedRef, useDirection, useUID } from '../../hooks';
import { StyledLabel } from '../Label';
import { calculateFontSize } from '../../styles';

export interface RadioCheckProps extends BaseProps, NoChildrenProp {
  /**
   * Sets DOM id for the control and associates label element via 'for' attribute.
   * If an id is not pass, a random id will be generated for any render.
   */
  id?: FormControlProps['id'];
  /** Pass a string or a fragment with an Icon and string. */
  label?: FormControlProps['label'];
  /** Indicate if the field is required. The browser defaults to false. */
  required?: FormControlProps['required'];
  /** Disable the control. The browser defaults to false. */
  disabled?: FormControlProps['disabled'];
  /** Makes the input non editable and non clickable. The browser defaults to false. */
  readOnly?: FormControlProps['readOnly'];
  /** Set visual state based on a validation state. */
  status?: FormControlProps['status'];
  /** Sets html name attribute for the underlying control. Useful for mapping to a data field. */
  name?: FormControlProps['name'];
  /** Sets the type of input. */
  type: 'radio' | 'checkbox';
  /** Sets checked prop via onChange when using as controlled component. */
  checked?: boolean;
  /** Initialize checked prop when using as uncontrolled component. */
  defaultChecked?: boolean;
  /**
   * Sets control to an an indeterminate state.
   * @default false
   */
  indeterminate?: boolean;
  /**
   * Controls the styling of the RadioCheck.
   * @default 'simple'
   */
  variant?: 'simple' | 'card';
  /** Callback function for onClick events. */
  onClick?: MouseEventHandler<HTMLInputElement>;
  /** Callback function for onKeyDown events. */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  /** Ref forwarded to the underlying input. */
  ref?: Ref<HTMLInputElement>;
}

export const StyledPseudoRadioCheck = styled.div<Pick<RadioCheckProps, 'readOnly' | 'status'>>(
  props => {
    const {
      theme: {
        base: {
          spacing,
          palette: { 'primary-background': primaryBackground }
        },
        components: {
          'form-field': formField,
          'radio-check': {
            size,
            'touch-size': touchSize,
            'background-color': backgroundColor,
            'border-color': borderColor,
            'border-width': borderWidth
          }
        }
      },
      status
    } = props;

    const useBorderColor = status === 'error' ? formField.error['status-color'] : borderColor;
    const useBackgroundColor =
      status && formField[status]
        ? tryCatch(() => mix(0.8, primaryBackground, formField[status]['status-color']))
        : backgroundColor;

    return css`
      display: flex;
      flex-shrink: 0;
      position: relative;
      width: ${size};
      height: ${size};
      margin-inline-end: calc(${spacing} / 2);
      border: ${borderWidth} solid ${useBorderColor};
      background-color: ${useBackgroundColor};

      @media (pointer: coarse) {
        width: ${touchSize};
        height: ${touchSize};
      }

      &::after {
        content: '';
        display: none;
      }
    `;
  }
);

StyledPseudoRadioCheck.defaultProps = defaultThemeProp;

export const StyledRadioCheckInput = styled.input(props => {
  const {
    theme: {
      base: { 'border-radius': baseBorderRadius },
      components: {
        'form-control': {
          ':focus': { 'box-shadow': shadow, 'border-color': focusBorderColor },
          ':read-only': { 'background-color': readOnlyBackgroundColor }
        },
        'radio-check': {
          ':checked': {
            'background-color': checkedBackgroundColor,
            'border-color': checkedBorderColor
          }
        },
        checkbox: { 'border-radius': checkRadius },
        'radio-button': { 'border-radius': radioRadius }
      }
    }
  } = props;

  const backgroundColor = props.readOnly ? readOnlyBackgroundColor : checkedBackgroundColor;
  const foreground = tryCatch(() => readableColor(backgroundColor));

  const prcSelector = `+ ${StyledLabel} ${StyledPseudoRadioCheck}`;
  const { ltr } = useDirection();

  return css`
    ${hideVisually}

    ${!(props.disabled || props.readOnly) &&
    css`
      &:focus ${prcSelector} {
        box-shadow: ${shadow};
        border-color: ${focusBorderColor};
      }
    `}

    &:checked
      ${prcSelector},
      &:checked:disabled
      ${prcSelector},
      &[type='checkbox']:indeterminate
      ${prcSelector},
      &[type='checkbox']:indeterminate:disabled
      ${prcSelector} {
      border-color: ${props.readOnly ? 'inherit' : checkedBorderColor};
      background-color: ${backgroundColor};

      &::after {
        display: block;
      }
    }

    &[type='radio'] ${prcSelector}, &[type='radio'] ${prcSelector}::after {
      border-radius: ${radioRadius};
    }

    &[type='radio'] ${prcSelector}::after {
      margin: auto;
      width: 100%;
      height: 100%;
      background-color: ${foreground};
      transform: scale(0.4);
    }

    &[type='checkbox'] ${prcSelector} {
      border-radius: min(calc(${baseBorderRadius} * ${checkRadius}), 0.25rem);
    }

    &[type='checkbox']:not(:indeterminate) ${prcSelector} {
      &::after {
        width: 40%;
        height: 75%;
        ${ltr
          ? css`
              transform: rotate(45deg) translate(50%, -30%);
            `
          : css`
              transform: rotate(45deg) translate(-50%, 30%);
            `}
        border-right: 0.15em solid ${foreground};
        border-bottom: 0.15em solid ${foreground};
      }
    }

    &[type='checkbox']:indeterminate ${prcSelector} {
      display: flex;
      &::after {
        width: 90%;
        height: 0.15em;
        margin: auto;
        background-color: ${foreground};
      }
    }
  `;
});

StyledRadioCheckInput.defaultProps = defaultThemeProp;

export const StyledRadioCheck = styled.div<Pick<RadioCheckProps, 'disabled' | 'readOnly'>>(
  props => {
    const {
      disabled,
      readOnly,
      theme: {
        base,
        components: {
          'radio-check': {
            label: { color: labelColor, 'font-weight': labelFontWeight }
          },
          'form-control': {
            ':hover': { 'border-color': hoverBorderColor }
          }
        }
      }
    } = props;

    const fontSize = calculateFontSize(base['font-size'], base['font-scale']);

    return css`
      > ${StyledLabel} {
        cursor: pointer;
        display: flex;
        align-items: center;
        font-weight: ${labelFontWeight};
        word-break: break-word;
        font-size: ${fontSize.s};
        color: ${labelColor};
        margin: 0;
        min-height: ${base['hit-area']['mouse-min']};

        @media (pointer: coarse) {
          min-height: ${base['hit-area']['finger-min']};
        }
      }

      ${!(disabled || readOnly) &&
      css`
        &:not(:focus-within) > ${StyledLabel}:hover ${StyledPseudoRadioCheck} {
          border-color: ${hoverBorderColor};
        }
      `}
    `;
  }
);

StyledRadioCheck.defaultProps = defaultThemeProp;

export const StyledRadioCheckCard = styled.label<
  Pick<RadioCheckProps, 'disabled' | 'readOnly' | 'status'>
>(
  ({
    disabled,
    readOnly,
    status,
    theme: {
      base: { palette, shadow, spacing },
      components: {
        card,
        'form-field': formField,
        'radio-check': { label },
        'form-control': {
          ':hover': { 'border-color': hoverBorderColor }
        }
      }
    }
  }) => {
    const useBorderColor =
      status === 'error' ? formField.error['status-color'] : palette['border-line'];

    return css`
      min-width: min-content;
      align-items: start;
      cursor: pointer;
      background-color: ${card.background};
      border-radius: ${card['border-radius']};
      border: 0.0625rem solid ${useBorderColor};
      padding: ${spacing};

      ${disabled &&
      css`
        cursor: not-allowed;
      `}

      ${!(disabled || readOnly) &&
      css`
        :hover:not(:focus-within) {
          border-color: ${hoverBorderColor};
          ${StyledLabel} ${StyledPseudoRadioCheck} {
            border-color: ${hoverBorderColor};
          }
        }

        :focus-within {
          box-shadow: ${shadow.focus};
          border-color: transparent;
        }
      `}

    > ${StyledLabel} {
        display: grid;
        grid-template-columns: auto 1fr;
        color: ${label.color};
        font-weight: ${label['font-weight']};
      }
    `;
  }
);

StyledRadioCheckCard.defaultProps = defaultThemeProp;

const RadioCheck: FC<RadioCheckProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<RadioCheckProps>, ref: RadioCheckProps['ref']) => {
    const uid = useUID();
    const {
      type,
      id = uid,
      label,
      required = false,
      disabled = false,
      indeterminate = false,
      readOnly = false,
      variant = 'simple',
      onClick,
      onKeyDown,
      status,
      ...restProps
    } = props;

    const isRadio = type === 'radio';
    const card = variant === 'card';
    const newRef = useConsolidatedRef(ref);

    useEffect(() => {
      if (!isRadio && newRef.current) {
        newRef.current.indeterminate = !!indeterminate;
      }
    }, [newRef, indeterminate, isRadio]);

    return (
      <FormField
        as={card ? StyledRadioCheckCard : StyledRadioCheck}
        label={
          <>
            <StyledPseudoRadioCheck
              status={status}
              isRadio={isRadio}
              as={StyledFormControl}
              required={required}
              disabled={disabled}
              readOnly={readOnly}
            />
            {label}
          </>
        }
        labelAs={card ? 'div' : undefined}
        id={id}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        status={status}
        inline
        labelAfter
      >
        <StyledRadioCheckInput
          {...restProps}
          id={id}
          type={type}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          aria-readonly={readOnly}
          onClick={(e: MouseEvent<HTMLInputElement>) => {
            if (readOnly) e.preventDefault();

            onClick?.(e);
          }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (type === 'checkbox' && readOnly && e.key === ' ') {
              e.preventDefault();
            }
            if (type === 'radio' && readOnly && e.key.includes('Arrow')) {
              e.preventDefault();
            }

            onKeyDown?.(e);
          }}
          ref={newRef}
        />
      </FormField>
    );
  }
);

export default RadioCheck;
